import { ReactNode, useState } from 'react';
import { Dialog } from './dialog';
import { Button } from 'primereact/button';
import { CourseFeedbackPromptDto, PendingCertificateNameDto } from '../../general/dtos';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';
import { CertificateNameDialog } from './certificate-name-dialog';
import { CourseCompletionEmailDialog } from './course-completion-email-dialog';
import { CourseFeedbackDialog } from './course-feedback-dialog';
import '../../assets/course-completion.css';

export interface CourseCompletionOpportunity {
    course: CourseFeedbackPromptDto;
    certificate?: PendingCertificateNameDto;
    email: boolean;
    feedback: boolean;
}

/** A course can appear in several completion lists; present it only once. */
export function completionOpportunities(names: PendingCertificateNameDto[] = [], emails: CourseFeedbackPromptDto[] = [], reviews: CourseFeedbackPromptDto[] = []) {
    const result = new Map<string, CourseCompletionOpportunity>();
    const get = (course: CourseFeedbackPromptDto) => {
        if (!result.has(course.course_key)) result.set(course.course_key, { course, email: false, feedback: false });
        return result.get(course.course_key)!;
    };
    names.forEach(certificate => Object.assign(get(certificate), { certificate, email: true, feedback: certificate.offer_review }));
    emails.forEach(course => { get(course).email = true; });
    reviews.forEach(course => { get(course).feedback = true; });
    return [...result.values()];
}

type Action = 'certificate' | 'email' | 'feedback';
export function CourseCompletionDialog({ opportunity, sessionId, defaultEmail, onDone, onCertificateConfirmed, remaining = 1 }: {
    opportunity: CourseCompletionOpportunity; sessionId: string; defaultEmail: string; onDone: () => void;
    onCertificateConfirmed: () => void; remaining?: number;
}) {
    const parent = useFullscreenDialogParent();
    const { course, certificate, email, feedback } = opportunity;
    const [open, setOpen] = useState<Action | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState<Partial<Record<Action, boolean>>>({});
    const finish = (action: Action) => { setDone(current => ({ ...current, [action]: true })); setOpen(null); };
    const card = (action: Action, title: string, icon: string, description: string, label: string, content: ReactNode) => (
        <section className={`completion-card${done[action] ? ' completion-card-done' : ''}`} aria-labelledby={`completion-heading-${action}`}>
            <div className="completion-card-summary">
                <i className={`pi ${done[action] ? 'pi-check-circle' : icon} completion-card-icon`} aria-hidden="true" />
                <div className="completion-card-copy">
                    <h3 id={`completion-heading-${action}`}>{title}</h3>
                    <p>{description}</p>
                    {done[action] && <span role="status" className="completion-status">{action === 'certificate' ? 'Name confirmed' : action === 'email' ? 'Confirmation sent' : 'Feedback submitted'}</span>}
                </div>
                {!done[action] && <Button label={open === action ? 'Hide' : label} outlined disabled={busy}
                    aria-expanded={open === action} aria-controls={`completion-form-${action}`}
                    onClick={() => setOpen(current => current === action ? null : action)} />}
            </div>
            <div id={`completion-form-${action}`} hidden={open !== action || !!done[action]} className="completion-card-form">{content}</div>
        </section>
    );
    return <Dialog header="Course completed" visible modal appendTo={parent} baseZIndex={11000}
        className="course-completion-dialog" style={{ width: '49rem', maxWidth: '95vw' }} draggable={false}
        closable={!busy} closeOnEscape={!busy} onHide={() => { if (!busy) onDone(); }}
        footer={<Button label={remaining > 1 ? 'Next completed course' : 'Done'} icon="pi pi-check" disabled={busy} onClick={onDone} />}>
        <div className="completion-celebration"><i className="pi pi-check-circle" aria-hidden="true" /><div>
            <h2>{course.course_name}</h2><p>You have completed all lessons in this course. Well done!</p>
        </div></div>
        <p className="completion-intro">Before you go, choose any of the options below. Each action is optional.</p>
        <div className="completion-cards">
            {certificate && card('certificate', 'Your certificate', 'pi-verified', 'Check the name that will appear on your certificate.', 'Check name',
                <CertificateNameDialog embedded certificate={certificate} onBusyChange={setBusy}
                    onLater={() => setOpen(null)} onConfirmed={() => { finish('certificate'); onCertificateConfirmed(); }} />)}
            {!certificate && course.certificate_enabled === false && <p className="m-0 text-color-secondary">This course does not include a certificate.</p>}
            {email && card('email', 'Email your completion confirmation', 'pi-envelope', 'Keep a confirmation of your achievement in your inbox.', 'Email me',
                <CourseCompletionEmailDialog embedded course={course} defaultEmail={defaultEmail} onBusyChange={setBusy}
                    onSent={() => finish('email')} onDone={() => setOpen(null)} />)}
            {feedback && card('feedback', 'Tell us how it went', 'pi-star', 'Rate the course and optionally share written feedback.', 'Leave feedback',
                <CourseFeedbackDialog embedded course={course} sessionId={sessionId} onBusyChange={setBusy}
                    onDone={() => finish('feedback')} />)}
        </div>
        <p className="completion-outro">Closing this dialog will not undo your course completion.</p>
    </Dialog>;
}
